import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Plus, X, FolderKanban, CheckCircle2, ShieldCheck, Truck, Bot } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import ModalPortal from "../../components/ModalPortal";

const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-xl border border-slate-200 shadow-sm ${className}`}>{children}</div>
);
const STATUS_COLORS = {
  planning: "bg-slate-100 text-slate-600", in_progress: "bg-emerald-100 text-emerald-700",
  waiting_customer: "bg-amber-100 text-amber-700", quality_check: "bg-orange-100 text-orange-700",
  ready_for_delivery: "bg-cyan-100 text-cyan-700", delivered: "bg-emerald-100 text-emerald-700",
  completed: "bg-emerald-200 text-emerald-800", cancelled: "bg-rose-100 text-rose-700",
};
const M_STATUSES = ["pending", "in_progress", "done", "blocked"];

function CreateModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ title: "", service_category: "website", customer_email: "", target_date: "", order_ref: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.title.trim() || !form.customer_email.trim()) { toast.error("Title and customer email are required"); return; }
    setBusy(true);
    try {
      await api.post("/admin/ops/projects", { ...form, target_date: form.target_date || null, order_ref: form.order_ref || null });
      toast.success("Project workspace created with checklist"); onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const inp = "w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm";
  return (
    <ModalPortal>
      <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
        <div className="w-full max-w-md bg-white rounded-2xl p-6" onClick={(e)=>e.stopPropagation()} data-testid="opsprj-create-modal">
          <div className="flex justify-between items-center"><h3 className="font-bold text-slate-900">New project workspace</h3><button onClick={onClose}><X size={18} className="text-slate-400"/></button></div>
          <div className="mt-4 space-y-3">
            <label className="block text-xs text-slate-500">Project title<input value={form.title} onChange={set("title")} className={inp} data-testid="opsprj-title"/></label>
            <label className="block text-xs text-slate-500">Service category (auto-checklist)
              <select value={form.service_category} onChange={set("service_category")} className={inp} data-testid="opsprj-category">
                <option value="website">Website Development (18 steps)</option>
                <option value="seo">SEO Service (12 steps)</option>
                <option value="api">API Integration (11 steps)</option>
                <option value="other">Other (5 steps)</option>
              </select></label>
            <label className="block text-xs text-slate-500">Customer email<input value={form.customer_email} onChange={set("customer_email")} className={inp} data-testid="opsprj-email"/></label>
            <div className="grid grid-cols-2 gap-2">
              <label className="block text-xs text-slate-500">Target date<input type="date" value={form.target_date} onChange={set("target_date")} className={inp} data-testid="opsprj-target"/></label>
              <label className="block text-xs text-slate-500">Order ref (optional)<input value={form.order_ref} onChange={set("order_ref")} className={inp} data-testid="opsprj-orderref"/></label>
            </div>
          </div>
          <button onClick={submit} disabled={busy} className="mt-4 w-full py-2.5 rounded-xl bg-emerald-600 text-white font-bold text-sm" data-testid="opsprj-create-submit">
            {busy ? "Creating…" : "Create workspace"}</button>
        </div>
      </div>
    </ModalPortal>
  );
}

function ProjectDetail({ id, onClose, onChanged }) {
  const [p, setP] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const load = useCallback(() => { api.get(`/admin/ops/projects/${id}`).then(r=>setP(r.data)); }, [id]);
  useEffect(load, [load]);
  const aiExecute = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post(`/admin/ops/projects/${id}/ai-execute`);
      toast.success(`AI deliverable created: ${data.title} — review it in the AI Work tab`);
      load(); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "AI execution failed"); }
    finally { setAiBusy(false); }
  };
  const patchM = async (mid, body) => {
    try { const { data } = await api.patch(`/admin/ops/projects/${id}/milestones/${mid}`, body); setP(data); onChanged(); }
    catch { toast.error("Update failed"); }
  };
  const patchQa = async (key, passed) => {
    try { const { data } = await api.patch(`/admin/ops/projects/${id}/qa/${key}`, { passed }); setP(data); }
    catch { toast.error("Update failed"); }
  };
  const deliver = async () => {
    try { const { data } = await api.post(`/admin/ops/projects/${id}/deliver`); setP(data); onChanged(); toast.success("Delivered — customer notified"); }
    catch (e) { toast.error(e?.response?.data?.detail || "QA gate blocked delivery"); }
  };
  const setStatus = async (status) => {
    try { const { data } = await api.patch(`/admin/ops/projects/${id}`, { status }); setP(data); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <ModalPortal>
      <div className="fixed inset-0 z-[100] bg-black/60 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
        <div className="w-full max-w-3xl bg-white rounded-2xl p-6 my-6" onClick={(e)=>e.stopPropagation()} data-testid="opsprj-detail">
          {!p ? <Loader2 className="animate-spin mx-auto my-10 text-emerald-600"/> : (<>
            <div className="flex justify-between items-start">
              <div>
                <div className="font-mono text-xs text-emerald-700">{p.number} · {p.service_category}</div>
                <h3 className="font-bold text-slate-900 text-lg">{p.title}</h3>
                <div className="text-xs text-slate-500 mt-0.5">{p.customer_email} · target {p.target_date || "—"} · {p.completion_pct}% complete</div>
              </div>
              <button onClick={onClose}><X size={18} className="text-slate-400"/></button>
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <select value={p.status} onChange={(e)=>setStatus(e.target.value)} className="px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs font-bold" data-testid="opsprj-status-select">
                {["planning","in_progress","waiting_customer","quality_check","ready_for_delivery","cancelled"].map(s=><option key={s} value={s}>{s.replace(/_/g," ")}</option>)}
              </select>
              <button onClick={aiExecute} disabled={aiBusy} className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-bold inline-flex items-center gap-1 disabled:opacity-50" data-testid="opsprj-ai-execute">
                {aiBusy ? <Loader2 size={12} className="animate-spin"/> : <Bot size={12}/>} {aiBusy ? "AI working…" : "AI Execute"}</button>
              <button onClick={deliver} className="px-3 py-1.5 rounded-lg bg-orange-500 text-white text-xs font-bold inline-flex items-center gap-1" data-testid="opsprj-deliver"><Truck size={12}/> Deliver (QA-gated)</button>
              <div className="ml-auto h-2 w-40 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-emerald-500 to-orange-400" style={{width:`${p.completion_pct}%`}}/></div>
            </div>

            <h4 className="font-bold text-slate-800 text-sm mt-5 mb-2">Milestones</h4>
            <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
              {p.milestones.map(m => (
                <div key={m.id} className="flex items-center gap-2 text-sm bg-slate-50 rounded-lg px-3 py-2">
                  <span className={`w-2 h-2 rounded-full shrink-0 ${m.status==="done"?"bg-emerald-500":m.status==="in_progress"?"bg-amber-500":m.status==="blocked"?"bg-rose-500":"bg-slate-300"}`}/>
                  <span className={`flex-1 ${m.status==="done"?"text-slate-400 line-through":"text-slate-700"}`}>{m.title}
                    {m.customer_approved_at && <span className="ml-1.5 text-[10px] text-emerald-600 font-bold">✓ customer approved</span>}
                  </span>
                  <select value={m.status} onChange={(e)=>patchM(m.id, { status: e.target.value })}
                    className="px-2 py-1 rounded border border-slate-200 text-[11px]" data-testid={`opsprj-ms-${m.id.slice(0,6)}`}>
                    {M_STATUSES.map(s=><option key={s} value={s}>{s.replace("_"," ")}</option>)}
                  </select>
                </div>
              ))}
            </div>

            <h4 className="font-bold text-slate-800 text-sm mt-5 mb-2 inline-flex items-center gap-1.5"><ShieldCheck size={14} className="text-emerald-600"/> QA delivery gate <span className="text-[10px] text-slate-400 font-normal">(critical checks must pass before delivery)</span></h4>
            <div className="grid sm:grid-cols-2 gap-1.5">
              {p.qa_checks.map(c => (
                <label key={c.key} className={`flex items-center gap-2 text-xs rounded-lg px-3 py-2 border ${c.passed===true?"bg-emerald-50 border-emerald-200":c.critical?"bg-orange-50/50 border-orange-200":"bg-slate-50 border-slate-100"}`}>
                  <input type="checkbox" checked={c.passed===true} onChange={(e)=>patchQa(c.key, e.target.checked)} className="accent-emerald-600" data-testid={`opsprj-qa-${c.key}`}/>
                  <span className="flex-1 text-slate-700">{c.label}</span>
                  {c.critical && <span className="text-[9px] font-bold text-orange-600 uppercase">critical</span>}
                </label>
              ))}
            </div>

            {p.revisions?.length > 0 && (<>
              <h4 className="font-bold text-slate-800 text-sm mt-4 mb-2">Revision requests</h4>
              {p.revisions.map(r => <div key={r.id} className="text-xs text-slate-600 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mb-1">“{r.note}” — {r.by}</div>)}
            </>)}
          </>)}
        </div>
      </div>
    </ModalPortal>
  );
}

export default function AdminOpsProjects() {
  const [rows, setRows] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const load = useCallback(() => { api.get("/admin/ops/projects").then(r=>setRows(r.data.rows)).catch(()=>toast.error("Failed")); }, []);
  useEffect(load, [load]);
  return (
    <div data-testid="aiops-projects">
      <div className="flex justify-end mb-3">
        <button onClick={()=>setCreateOpen(true)} className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-xs font-bold inline-flex items-center gap-1.5" data-testid="opsprj-new"><Plus size={13}/> New project workspace</button>
      </div>
      {!rows ? <Loader2 className="animate-spin mx-auto my-10 text-emerald-600"/> :
        rows.length === 0 ? <Card className="p-8 text-center text-sm text-slate-400"><FolderKanban size={24} className="mx-auto mb-2 text-slate-300"/>No project workspaces yet. Create one — the service checklist is generated automatically.</Card> : (
        <div className="grid md:grid-cols-2 gap-3">
          {rows.map(p => (
            <Card key={p.id} className="p-4 cursor-pointer hover:border-emerald-300 transition-colors" >
              <div onClick={()=>setDetail(p.id)} data-testid={`opsprj-open-${p.number}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-[11px] text-emerald-700">{p.number}</span>
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${STATUS_COLORS[p.status]}`}>{p.status.replace(/_/g," ")}</span>
                </div>
                <div className="font-bold text-slate-900 mt-1">{p.title}</div>
                <div className="text-xs text-slate-500">{p.customer_email} · {p.service_category} · target {p.target_date || "—"}</div>
                <div className="mt-2.5 flex items-center gap-2">
                  <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-emerald-500 to-orange-400" style={{width:`${p.completion_pct}%`}}/></div>
                  <span className="text-xs font-bold text-slate-600">{p.completion_pct}%</span>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
      {createOpen && <CreateModal onClose={()=>setCreateOpen(false)} onCreated={()=>{ setCreateOpen(false); load(); }}/>}
      {detail && <ProjectDetail id={detail} onClose={()=>setDetail(null)} onChanged={load}/>}
    </div>
  );
}
